import { LocationSettings } from "@/utils/location-settings";
import { isKashuApp } from "../utils";

export type PrimaryScaleKey =
	| 50
	| 100
	| 200
	| 300
	| 400
	| 500
	| 600
	| 700
	| 800
	| 950;

export type PrimaryPalette = Record<PrimaryScaleKey, string>;

const SHADES: PrimaryScaleKey[] = [
	50, 100, 200, 300, 400, 500, 600, 700, 800, 950,
];

const MIX_POINTS: Record<PrimaryScaleKey, number> = {
	50: 0.92,
	100: 0.82,
	200: 0.65,
	300: 0.48,
	400: 0.3,
	500: 0.0,
	600: 0.2,
	700: 0.4,
	800: 0.58,
	950: 0.78,
};

type RGB = { r: number; g: number; b: number };

const clamp255 = (n: number) => Math.round(Math.max(0, Math.min(255, n)));

const hexMatcher = /^#([0-9a-f]{6})$/i;

function hexToRgb(hex: string) {
	const hexMatch = hexMatcher.exec(hex);
	if (!hexMatch) return null;

	const h = hexMatch[1];

	return {
		r: Number.parseInt(h.slice(0, 2), 16),
		g: Number.parseInt(h.slice(2, 4), 16),
		b: Number.parseInt(h.slice(4, 6), 16),
	};
}

function rgbToHex({ r, g, b }: RGB) {
	const toHex = (v: number) => clamp255(v).toString(16).padStart(2, "0");
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function hexToRgba(hex: string, alpha: number) {
	const rgb = hexToRgb(hex);
	if (!rgb) return hex;
	return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function mixRgb(c1: RGB, c2: RGB, t: number) {
	return {
		r: c1.r + (c2.r - c1.r) * t,
		g: c1.g + (c2.g - c1.g) * t,
		b: c1.b + (c2.b - c1.b) * t,
	};
}

function generatePalette(baseHex: string) {
	const base = hexToRgb(baseHex);
	if (!base) return null;

	const white: RGB = { r: 255, g: 255, b: 255 };
	const black: RGB = { r: 0, g: 0, b: 0 };

	const palette = {} as PrimaryPalette;

	for (const shade of SHADES) {
		if (shade === 500) {
			palette[shade] = baseHex.toLowerCase();
			continue;
		}

		const t = MIX_POINTS[shade];
		const from = shade < 500 ? white : base;
		const to = shade < 500 ? base : black;

		palette[shade] = rgbToHex(mixRgb(from, to, shade < 500 ? 1 - t : t));
	}

	return palette;
}

function isValidHex(s?: string | null) {
	return typeof s === "string" && /^#([0-9a-f]{6})$/i.test(s);
}

export function applyPalette(baseHex: string) {
	const palette = generatePalette(baseHex);
	if (!palette || typeof document === "undefined") return;

	const root = document.documentElement;

	for (const [k, v] of Object.entries(palette)) {
		root.style.setProperty(`--primary-${k}`, v);
	}

	root.style.setProperty("--textDark", palette[950]);
	root.style.setProperty("--textLight", hexToRgba(palette[950], 0.75));
	root.style.setProperty("--primary", palette[500]);
	root.style.setProperty("--secondary", palette[800]);
	root.style.setProperty("--secondaryText", "#ffffff");
	root.style.setProperty("--border", hexToRgba(palette[950], 0.2));
	root.style.setProperty("--border-focus", palette[300]);
	root.style.setProperty("--darkButtonText", "#ffffff");
}

export function clearPalette() {
	const root = document.documentElement;

	for (const k of [50, 100, 200, 300, 400, 500, 600, 700, 800, 950]) {
		root.style.removeProperty(`--primary-${k}`);
	}

	root.style.removeProperty("--textDark");
	root.style.removeProperty("--textLight");
	root.style.removeProperty("--primary");
	root.style.removeProperty("--secondary");
	root.style.removeProperty("--secondaryText");
}

export function applyApplicationTheme(locationSettings: LocationSettings) {
	const isKashu = isKashuApp();
	if (isKashu) return;

	const c = locationSettings.primary_color?.trim() ?? "";
	const base = isValidHex(c) ? c.toLowerCase() : null;

	if (base) {
		applyPalette(base);
	}
}
